import { apiAxios } from '@/api'
import { fetchIfNeededWrapper, fetchWrapper } from '@/reducers/fetch'
import {
  ChartAPIResponse,
  INSIGHTS_TYPE,
  InsightsCashflowResponse,
  InsightsKeyWidget,
  InsightsRowAPIResponse,
  InsightsTransactionsAPIResponse,
  SummaryAPIResponse,
  UncategorizedTransactionResponse,
} from './utils'
import {
  addInsightSummary,
  selectInsightSummary,
} from '@/reducers/insights.slice'

export const FETCH_INSIGHTS_BARCHART_DATA_KEY =
  'FETCH_INSIGHTS_BARCHART_DATA_KEY'
export const fetchInsightsBarchartData = (
  type: INSIGHTS_TYPE,
  startDate: string,
  endDate: string
) =>
  fetchWrapper({
    fetchKey: FETCH_INSIGHTS_BARCHART_DATA_KEY,
    defaultErrorMessage:
      'Unable to get financial insights data for the bar chart.',
    fetchFunction: async () => {
      const res = await apiAxios.get<ChartAPIResponse[]>(
        '/finances/api/v1/financial_insights/barchart',
        { params: { type, startDate, endDate } }
      )
      return res.data
    },
  })

export const FETCH_INSIGHTS_SUMMARY_KEY = (
  type: INSIGHTS_TYPE,
  month: number,
  year: number
) => `FETCH_INSIGHTS_SUMMARY_KEY_${type}_${month}_${year}`

export const fetchInsightsSummary = (
  type: INSIGHTS_TYPE,
  month: number,
  year: number
) => {
  const fetchKey = FETCH_INSIGHTS_SUMMARY_KEY(type, month, year)

  return fetchIfNeededWrapper({
    fetchKey,
    defaultErrorMessage:
      'Unable to get financial insights data for the summary.',
    defaultValueSelector: (state) => selectInsightSummary(state, fetchKey),
    fetchFunction: async (dispatch) => {
      const res = await apiAxios.get<SummaryAPIResponse[]>(
        '/finances/api/v1/financial_insights/summary',
        { params: { type, month, year } }
      )

      dispatch(addInsightSummary({ key: fetchKey, response: res.data }))

      return res.data
    },
  })
}

export const FETCH_INSIGHTS_TABLE_DROPDOWN_KEY =
  'FETCH_INSIGHTS_TABLE_DROPDOWN_KEY'
export const fetchInsightsTableDropdownOptions = (type: INSIGHTS_TYPE) =>
  fetchWrapper({
    fetchKey: FETCH_INSIGHTS_TABLE_DROPDOWN_KEY,
    defaultErrorMessage:
      'Unable to get financial insights data for the breakdown.',
    fetchFunction: async () => {
      const res = await apiAxios.get<number[]>(
        '/finances/api/v1/financial_insights/uitable/year-dropdown-options',
        { params: { type } }
      )
      return res.data
    },
  })

export const FETCH_INSIGHTS_TABLE_DATA_KEY = 'FETCH_INSIGHTS_BREAKDOWN_KEY'
export const fetchInsightsTableData = (
  type: INSIGHTS_TYPE,
  startDate: string,
  endDate: string
) =>
  fetchWrapper({
    fetchKey: FETCH_INSIGHTS_TABLE_DATA_KEY,
    defaultErrorMessage:
      'Unable to get financial insights data for the breakdown.',
    fetchFunction: async () => {
      const res = await apiAxios.get<InsightsRowAPIResponse[]>(
        '/finances/api/v1/financial_insights/uitable',
        { params: { type, startDate, endDate } }
      )
      return res.data
    },
  })

export const FETCH_INSIGHTS_CATEGORY_TRANSACTIONS_KEY =
  'FETCH_INSIGHTS_CATEGORY_TRANSACTIONS_KEY'
export const fetchInsightsCategoryTransactions = (
  transactionCategoryId: number,
  startDate: string,
  endDate: string
) =>
  fetchWrapper({
    fetchKey: FETCH_INSIGHTS_TABLE_DATA_KEY,
    defaultErrorMessage:
      'Unable to get financial insights data for the breakdown.',
    fetchFunction: async () => {
      const res = await apiAxios.get<InsightsTransactionsAPIResponse[]>(
        `/finances/api/v1/financial_insights/transaction_categories/${transactionCategoryId}/transactions`,
        { params: { startDate, endDate } }
      )
      return res.data
    },
  })

export const FETCH_INSIGHTS_UNCATEGORIZED_TRANSACTIONS_KEY =
  'FETCH_INSIGHTS_UNCATEGORIZED_TRANSACTIONS_KEY'
export const fetchInsightsUncategorizedTransactionsData = (endDate: string) =>
  fetchWrapper({
    fetchKey: FETCH_INSIGHTS_TABLE_DATA_KEY,
    defaultErrorMessage:
      'Unable to get financial insights data for the uncategorized transactions',
    fetchFunction: async () => {
      const res = await apiAxios.get<UncategorizedTransactionResponse>(
        '/finances/api/v1/financial_insights/uncategorized_transactions',
        { params: { endDate } }
      )
      return res.data
    },
  })

export const FETCH_INSIGHTS_CASHFLOW_BALANCE_FOR_DEPOSITORY_ACCTS =
  'FETCH_INSIGHTS_CASHFLOW_BALANCE_FOR_DEPOSITORY_ACCTS'

export const fetchInsightsCashflowBalanceForDepositoryAccts = () =>
  fetchWrapper({
    fetchKey: FETCH_INSIGHTS_CASHFLOW_BALANCE_FOR_DEPOSITORY_ACCTS,
    defaultErrorMessage:
      'Unable to get cashflow data for depository financial accounts',
    fetchFunction: async () => {
      const res = await apiAxios.get<InsightsCashflowResponse>(
        '/finances/api/v1/financial_insights/cashflow_balance'
      )
      return res.data
    },
  })

export const FETCH_INSIGHTS_WIDGET_KEY = 'FETCH_INSIGHTS_WIDGET_KEY'

export const fetchInsightsWidgetKeys = () =>
  fetchWrapper({
    fetchKey: FETCH_INSIGHTS_WIDGET_KEY,
    defaultErrorMessage: 'Unable to get widget keys',
    fetchFunction: async () => {
      const res = await apiAxios.get<InsightsKeyWidget>(
        '/finances/api/v1/financial_insights/get_financial_insights_widget_key'
      )
      return res.data
    },
  })
